import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@siliconx/shared/ui/utils';

export interface ProfileTabDef {
  id: string;
  label: string;
  content: ReactNode;
}

/**
 * SIL-2214: accessible tab strip for the profile (Settings) page. WAI-ARIA
 * tabs pattern: tablist/tab/tabpanel roles, roving tabindex, arrow/Home/End
 * keys move focus and activate. Panels stay mounted (hidden when inactive) so
 * unsaved edits in one tab survive a tab switch.
 */
export function ProfileTabs({
  tabs,
  activeId,
  onChange,
}: {
  tabs: ProfileTabDef[];
  activeId: string;
  onChange: (id: string) => void;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else return;
    e.preventDefault();
    const id = tabs[next]!.id;
    onChange(id);
    refs.current[id]?.focus();
  }

  return (
    <div className="space-y-6">
      <div
        role="tablist"
        aria-label="Settings sections"
        className="flex gap-1 overflow-x-auto border-b border-border text-sm font-semibold"
      >
        {tabs.map((tab, i) => {
          const active = tab.id === activeId;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                refs.current[tab.id] = el;
              }}
              type="button"
              role="tab"
              id={`profile-tab-${tab.id}`}
              aria-selected={active}
              aria-controls={`profile-panel-${tab.id}`}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(tab.id)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className={cn(
                '-mb-px shrink-0 whitespace-nowrap border-b-2 px-3.5 py-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                active ? 'border-brand-600 text-brand-700' : 'border-transparent text-muted-foreground',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`profile-panel-${tab.id}`}
          aria-labelledby={`profile-tab-${tab.id}`}
          hidden={tab.id !== activeId}
          className="space-y-6"
        >
          {tab.content}
        </div>
      ))}
    </div>
  );
}
